import React from "react";
import { Anchor, Ship, Wrench, Waves, Star, ArrowRight, ShieldCheck, MapPin, Sparkles, Zap, MessagesSquare, Download, Smartphone } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useI18n } from "@/lib/i18n";
import { useInstallPrompt } from "@/lib/pwa";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/35609615/pexels-photo-35609615.jpeg";
const OWNER_IMG = "https://images.unsplash.com/photo-1678122878191-79b60410779f";
const PRO_IMG = "https://images.pexels.com/photos/3823425/pexels-photo-3823425.jpeg";
const COAST = "https://images.pexels.com/photos/38510508/pexels-photo-38510508.jpeg";

const PORTS = ["Port Vell", "Puerto Banús", "Marina Ibiza", "Puerto Portals", "Marina Alicante", "Puerto Sherry", "Marina Coruña", "Marina Lanzarote"];

export default function Landing() {
  const nav = useNavigate();
  const { t, lang, setLang } = useI18n();
  const { canInstall, install, installed } = useInstallPrompt();
  const doInstall = async () => {
    const ok = await install();
    if (canInstall === false && !installed) toast.info(lang === "es" ? "Abre en Safari > Compartir > Añadir a pantalla de inicio" : "Open in Safari > Share > Add to Home Screen");
    else if (ok) toast.success(lang === "es" ? "¡App instalada!" : "App installed!");
  };
  return (
    <div className="hub-cream min-h-screen">
      <Navbar />
      {/* Language switch */}
      <div className="fixed bottom-4 right-4 z-50 flex gap-1 bg-white/80 backdrop-blur border border-[#0B1D3A]/10 rounded-full p-1 shadow-lg">
        <button onClick={() => setLang("es")} data-testid="lang-es" className={`text-xs px-3 py-1 rounded-full font-display font-semibold ${lang === "es" ? "bg-[#0B1D3A] text-white" : "text-[#0B1D3A]"}`}>ES</button>
        <button onClick={() => setLang("en")} data-testid="lang-en" className={`text-xs px-3 py-1 rounded-full font-display font-semibold ${lang === "en" ? "bg-[#0B1D3A] text-white" : "text-[#0B1D3A]"}`}>EN</button>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16 md:pb-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="hub-pill hub-sand text-[#0B1D3A] mb-6">
              <Waves size={12}/> {t("hero.badge")}
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#0B1D3A]">
              {t("hero.title1")}<br/>
              <span className="italic hub-coral">{t("hero.title2Prefix")}</span> {t("hero.title2")}
            </h1>
            <p className="mt-6 text-lg text-[#5C6B7D] max-w-xl leading-relaxed">
              {t("hero.desc")} <span className="font-semibold text-[#0B1D3A]">{t("hero.desc2")}</span>{t("hero.desc3")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => nav("/register?role=owner")} className="hub-btn-primary flex items-center gap-2" data-testid="hero-cta-owner">
                <Ship size={16}/> {t("hero.ctaOwner")}
                <ArrowRight size={16}/>
              </button>
              <button onClick={() => nav("/register?role=professional")} className="hub-btn-coral hub-btn-primary flex items-center gap-2" data-testid="hero-cta-pro">
                <Wrench size={16}/> {t("hero.ctaPro")}
              </button>
              {!installed && (
                <button onClick={doInstall} className="rounded-full px-5 py-3 border-2 border-[#0B1D3A] text-[#0B1D3A] font-display font-semibold flex items-center gap-2 hover:bg-[#0B1D3A] hover:text-white transition-colors" data-testid="install-app-btn">
                  <Smartphone size={16}/> {t("install.app")}
                </button>
              )}
            </div>
            <div className="mt-10 flex items-center gap-6 text-[#5C6B7D] text-sm flex-wrap">
              <div className="flex items-center gap-2"><ShieldCheck size={16} className="hub-coral"/> {t("hero.verified")}</div>
              <div className="flex items-center gap-2"><Zap size={16} className="hub-coral"/> {t("hero.offers")}</div>
              <div className="flex items-center gap-2"><MapPin size={16} className="hub-coral"/> {t("hero.spain")}</div>
            </div>
          </div>
          <div className="md:col-span-5 relative">
            <div className="relative rounded-[28px] overflow-hidden aspect-[4/5] hub-card p-0">
              <img src={HERO} className="absolute inset-0 w-full h-full object-cover" alt="Marina" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0B1D3A]/70 via-[#0B1D3A]/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="hub-pill bg-white/20 backdrop-blur text-white mb-3">
                  <Star size={11} className="fill-white"/> 4.9 media de profesionales
                </div>
                <div className="font-serif text-3xl leading-tight">Del amarre al mar,<br/>sin llamar a nadie.</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hub-card p-4 max-w-[240px] hidden md:block">
              <div className="text-[10px] uppercase tracking-widest font-display text-[#5C6B7D]">Última solicitud</div>
              <div className="mt-1 font-serif text-lg">Antifouling · Puerto Portals</div>
              <div className="text-xs text-[#5C6B7D]">3 ofertas en 28 minutos</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
          <div className="md:col-span-6">
            <div className="hub-pill hub-sand text-[#0B1D3A] mb-4"><Sparkles size={12}/> Cómo funciona</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0B1D3A] tracking-tight">Un flujo pensado<br/>para dos partes.</h2>
          </div>
          <div className="md:col-span-6 text-[#5C6B7D]">Nosotros somos el puente. Ni comisiones ocultas, ni intermediarios ruidosos. Solo dos personas que se necesitan, hablando en el mismo puerto.</div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="hub-card p-8 md:p-10 relative overflow-hidden">
            <img src={OWNER_IMG} className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-20" alt="" />
            <div className="relative">
              <div className="w-11 h-11 rounded-full bg-[#0B1D3A] text-white flex items-center justify-center mb-5"><Ship size={18}/></div>
              <div className="font-display text-xs uppercase tracking-[0.2em] text-[#5C6B7D]">Propietario</div>
              <h3 className="font-serif text-3xl mt-1">Publica y recibe, no busques.</h3>
              <ol className="mt-6 space-y-4 text-[#0B1D3A]">
                {["Cuenta qué necesitas: servicio, puerto y barco.", "Los mejores profesionales de tu zona te ofertan.", "Chateas, aceptas y coordinas la cita.", "Valoras el trabajo. Sin llamadas, sin sorpresas."].map((t, i) => (
                  <li key={t} className="flex gap-3">
                    <span className="w-6 h-6 rounded-full hub-sand text-[#0B1D3A] text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">{i+1}</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
          <div className="hub-card p-8 md:p-10 relative overflow-hidden">
            <img src={PRO_IMG} className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-20" alt="" />
            <div className="relative">
              <div className="w-11 h-11 rounded-full bg-[#FF6B57] text-white flex items-center justify-center mb-5"><Wrench size={18}/></div>
              <div className="font-display text-xs uppercase tracking-[0.2em] text-[#5C6B7D]">Profesional</div>
              <h3 className="font-serif text-3xl mt-1">Trabajos que llegan solos.</h3>
              <ol className="mt-6 space-y-4 text-[#0B1D3A]">
                {["Configuras tus servicios y zona (comarca o provincia).", "Recibes un feed inteligente con los trabajos que encajan.", "Envías tu oferta con un clic: precio, duración y fecha.", "Cierras el trabajo directamente en el chat. Ganancia 100%."].map((t, i) => (
                  <li key={t} className="flex gap-3">
                    <span className="w-6 h-6 rounded-full hub-sand text-[#FF6B57] text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">{i+1}</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* PORTS STRIP */}
      <section className="hub-navy py-16 grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="hub-pill bg-white/10 text-white mb-4"><MapPin size={12}/> Toda España</div>
              <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight">Desde el Cantábrico<br/>hasta Canarias.</h2>
              <p className="text-white/70 mt-4 max-w-md">Cobertura en más de 17 provincias costeras y las principales marinas del país.</p>
            </div>
            <div className="md:col-span-7 grid grid-cols-2 gap-3">
              {PORTS.map((p) => (
                <div key={p} className="border border-white/15 rounded-xl px-4 py-3 flex items-center gap-3 text-white/90 hover:border-[#FF6B57]/60 transition-colors">
                  <Anchor size={14} className="text-[#FF6B57]"/>
                  <span className="font-display text-sm">{p}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust / Coast */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <img src={COAST} className="rounded-2xl w-full h-[420px] object-cover" alt="Costa" />
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <div className="hub-pill hub-sand text-[#0B1D3A] mb-4"><MessagesSquare size={12}/> Comunicación segura</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0B1D3A] tracking-tight leading-tight">Chat integrado con filtro anti-fuga.</h2>
            <p className="text-[#5C6B7D] mt-5 leading-relaxed">Los intentos de compartir teléfono, email o redes se bloquean automáticamente. Todo lo que se acuerda queda registrado en la plataforma — para tu tranquilidad y la del profesional.</p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[["+17", "provincias"], ["+100", "puertos"], ["0%", "comisión"], ["24h", "media de oferta"]].map((s)=> (
                <div key={s[1]} className="hub-card p-5" translate="no">
                  <div className="font-serif text-4xl text-[#FF6B57]" translate="no" data-notranslate>{s[0]}</div>
                  <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display mt-1">{s[1]}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="text-center mb-12">
          <div className="hub-pill hub-sand text-[#0B1D3A] mb-3 inline-flex"><Star size={12}/> Lo que dicen los primeros usuarios</div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#0B1D3A] tracking-tight">Confianza que se lee.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { name: "Marc E.", role: "Profesional · CleanBoat BCN", quote: "En dos semanas me llegaron 8 solicitudes en Port Vell. Un cliente lo pillé al día. Nunca imaginé cerrar tan rápido sin salir a buscar." },
            { name: "Toni V.", role: "Profesional · Palma Refit", quote: "La app filtra por comarca y solo me llegan los trabajos que puedo hacer. Es como tener un comercial 24 horas sin comisión." },
            { name: "Sofía G.", role: "Propietaria · Puerto Banús", quote: "Publiqué que necesitaba pulir el gelcoat y en 3 horas tenía 4 presupuestos. Elegí uno con vídeo de sus trabajos y quedé fascinada." },
          ].map((t) => (
            <div key={t.name} className="hub-card p-6">
              <div className="flex gap-0.5 mb-3">{Array.from({length: 5}).map((_,i) => <Star key={i} size={14} className="fill-[#FF6B57] text-[#FF6B57]"/>)}</div>
              <p className="text-[#0B1D3A] leading-relaxed italic">"{t.quote}"</p>
              <div className="mt-4 pt-4 border-t border-[#0B1D3A]/10">
                <div className="font-display font-semibold text-sm">{t.name}</div>
                <div className="text-xs text-[#5C6B7D]">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="hub-navy rounded-3xl p-10 md:p-16 relative overflow-hidden">
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <h3 className="font-serif text-4xl md:text-5xl text-white leading-tight">Tu barco merece <span className="italic hub-coral">un buen equipo</span>.</h3>
              <p className="text-white/70 mt-3 max-w-xl">Regístrate gratis. Recibes ofertas en minutos y decides tú, sin presiones.</p>
            </div>
            <div className="md:col-span-4 flex md:justify-end gap-3">
              <button onClick={() => nav("/register")} className="hub-btn-coral hub-btn-primary" data-testid="cta-register-final">Crear cuenta</button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#0B1D3A]/10 py-8 text-center text-sm text-[#5C6B7D] font-display">
        <div className="flex items-center justify-center gap-6 flex-wrap mb-2">
          <Link to="/faq" className="hover:text-[#0B1D3A]" data-testid="footer-faq">FAQ</Link>
          <Link to="/legal" className="hover:text-[#0B1D3A]" data-testid="footer-legal">Aviso Legal</Link>
          <Link to="/legal" className="hover:text-[#0B1D3A]" data-testid="footer-privacy">Privacidad</Link>
          <Link to="/legal" className="hover:text-[#0B1D3A]" data-testid="footer-cookies">Cookies</Link>
        </div>
        {t("footer")}
      </footer>
    </div>
  );
}
